/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { LoginStore } from '@boxx/login-core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Platform } from '@ionic/angular';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { LocalStorageNativeService } from 'src/services/NativeLocalStorage.service';
import { TELMEX_BRAND, TOTALPLAY_BRAND } from '../mocks/brand-data';
import { VENTASCLICK_BRAND } from '../mocks/brand-data-prod';

@Injectable({
  providedIn: 'root',
})
export class DynamicBrandingService {
  //for testing, remove when dynamic brandig feature is approved
  private themeChangedSubject = new BehaviorSubject<string>('');
  private myUserEmail = new BehaviorSubject<string>('');
  private appCopies = new BehaviorSubject<boolean>(false);

  private isCordovaOrCapacitor = false;
  private brandName: string;
  private business_id: number;

  //in order to re-save in LocalStorge (LS)
  // because after logout LS is (or might be) reseted
  private savedBranding: string;

  private originalPrimaryColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--ion-color-primary')
    .trim();
  private originalPrimaryColorRgb = getComputedStyle(document.documentElement)
    .getPropertyValue('--ion-color-primary-rgb')
    .trim();
  private originalPrimaryColorContrast = getComputedStyle(document.documentElement)
    .getPropertyValue('--ion-color-primary-contrast')
    .trim();
  private originalSecondaryColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--ion-color-secondary')
    .trim();
  private originalSecondaryColorRgb = getComputedStyle(document.documentElement)
    .getPropertyValue('--ion-color-secondary-rgb')
    .trim();
  private originalSecondaryColorContrast = getComputedStyle(document.documentElement)
    .getPropertyValue('--ion-color-secondary-contrast')
    .trim();
  private originalSecondaryTextColor: string;
  private originalTertiaryColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--ion-color-tertiary')
    .trim();
  private originalTertiaryColorRgb = getComputedStyle(document.documentElement)
    .getPropertyValue('--ion-color-tertiary-rgb')
    .trim();
  private originalTertiaryColorContrast = getComputedStyle(document.documentElement)
    .getPropertyValue('--ion-color-tertiary-contrast')
    .trim();
  private originalPrimaryTextColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--ion-text-color')
    .trim();




  private originalButtonColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--ion-color-button')
    .trim();

  private originalTabColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--ion-color-tab')
    .trim();

  private originalTabActiveColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--ion-color-tab-active')
    .trim();

  private originalPanelTextColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--ion-color-panel-text')
    .trim();

  private originalContrastTextColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--ion-color-contrast-text')
    .trim();

  private originalDefaultLetterTextColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--ion-color-default-text')
    .trim();   

  private originalDefaultLetterBgColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--ion-color-default-bg')
    .trim();



  //COLORS
  private myPrimaryColor: string;
  private myPrimaryColorRgb: string;
  private myPrimaryColorContrast: string;
  private mySecondaryColor: string;
  private mySecondaryColorRgb: string;
  private mySecondaryColorContrast: string;
  private myTertiaryColor: string;
  private myTertiaryColorRgb: string;
  private myTertiaryColorContrast: string;
  private myPrimaryTextColor: string;
  private mySecondaryTextColor: string;

  // TEXTS/URLS/etc
  private myComunicationEmail: string;
  private myCountry: string;
  private myCurrency: string;
  private myHomePageUrl: string;
  private myLanguage: string;
  private myLogo: string;
  private myPartnerUrl: string;
  private myPrivacyUrl: string;
  private mySignupUrl: string;
  private mySupportEmail: string;
  private myTermsUrl: string;
  private myToplineId: string;
  private myToplineUid: string;
  private myTradename: string;
  private myUrlToReset: string;

  private myButtonColor: string;
  private myTabColor: string;
  private myPanelTextColor: string;
  private myContrastTextColor: string;
  private myDefaultLetterTextColor: string;
  private myDefaultLetterBgColor: string;
  private myTabActiveColor: string;


  private MyContactChartNotSpecifiedColor: string;
  private MyContactChartProspectColor: string;
  private MyContactChartClientColor: string;
  private MyCustomBackgroundColor: string;
  private MyCustomTitleColor: string;
  private MyCustomIconColor: string;
  private MyCustomCheckboxColor: string;
  private MyCustomLabelColor: string;
  private MyCuttonColorContrast: string;
  private MyCustomMenuColor: string;

  private MyCustomDynamicColorOne: string;
  private MyCustomDynamicColorTwo: string;
  private MyCustomDynamicColorThree: string;
  private MyCustomDynamicColorFour: string;
  private MyCustomDynamicColorFive: string;

  private mySupportUrl?: string;
  private myOptionSupport?: string;

  constructor(
    private storage: LocalStorageNativeService,
    private loginStore: LoginStore,
    private platform: Platform
  ) {
    this.platform.ready().then(() => {
      this.isCordovaOrCapacitor = this.platform.is('cordova') || this.platform.is('capacitor');

      this.listenAuthChanges();

      this.storage.getItem(BRANDING_LS_KEY).then(branding => {
        if (branding) {
          this.savedBranding = branding;
          this.setTheme(JSON.parse(this.savedBranding));
        } else {
          //HERE GOES THE DEFAULT THEME
          this.setTheme(VENTASCLICK_BRAND);
          //this.setTheme(GETNET_BRAND);
          //this.setTheme(TELMEX_BRAND);
        }
      });
    });
  }

  //for testing, remove when dynamic brandig feature is approved
  get userEmail$(): Observable<string> {
    return this.myUserEmail.asObservable();
  }
  //for testing, remove when dynamic brandig feature is approved
  set userEmail(email: string) {
    this.myUserEmail.next(!environment.production ? email : '');
  }
  //for testing, remove when dynamic brandig feature is approved
  get userEmail(): string {
    return !environment.production ? this.myUserEmail.getValue() : '';
  }

  get themeChange$(): Observable<string> {
    return this.themeChangedSubject.asObservable();
  }

  get appCopies$(): Observable<any> {
    return this.appCopies.asObservable();
  }

  get comunicationEmail() {
    return this.myComunicationEmail;
  }
  get country() {
    return this.myCountry;
  }
  get currency() {
    return this.myCurrency;
  }
  get homePageUrl() {
    return this.myHomePageUrl;
  }
  get language() {
    return this.myLanguage;
  }
  get logo() {
    return this.myLogo;
  }
  get partnerUrl() {
    return this.myPartnerUrl;
  }
  get privacyUrl() {
    return this.myPrivacyUrl;
  }
  get signupUrl() {
    return this.mySignupUrl;
  }
  get supportEmail() {
    return this.mySupportEmail;
  }

  get supportUrl() {
    return this.mySupportUrl;
  }
  get optionSupport() {
    return this.myOptionSupport;
  }

  get termsUrl() {
    return this.myTermsUrl;
  }
  get toplineId() {
    return this.myToplineId;
  }
  get toplineUid() {
    return this.myToplineUid;
  }
  get tradename() {
    return this.myTradename;
  }
  get urlToReset() {
    return this.myUrlToReset;
  }

  setTheme(parsedBranding?: Partial<IBrandigApiProps>) {
    if (!parsedBranding) {
      return;
    }
    this.brandName = parsedBranding.tradename;
    this.brandName = parsedBranding.tradename;
    this.storage.setItem(BRANDING_LS_KEY, JSON.stringify(parsedBranding));
    this.savedBranding = JSON.stringify(parsedBranding);
    this.setColors(parsedBranding);
    this.setCopies(parsedBranding);
    this.themeChangedSubject.next(parsedBranding.tradename);
    // if(parsedBranding.tradename.toUpperCase() === 'VENTASCLICK'){
    //     document.documentElement.style.setProperty(
    //       '--contact-chart-not_specified',
    //       '#fbba33'
    //     );
    //     document.documentElement.style.setProperty(
    //       '--contact-chart-prospect',
    //       '#ed6d3a'
    //     );
    //     document.documentElement.style.setProperty(
    //       '--contact-chart-client',
    //       '#218b9a'
    //     );
    // }

  }

  initialTheme(){
    this.setTheme(VENTASCLICK_BRAND);
  }

  setBrandTheme(brand: string){
    this.setTheme(VENTASCLICK_BRAND);
    // switch (brand.toUpperCase()) {
    //   case 'VENTASCLICK':
    //     this.setTheme(VENTASCLICK_BRAND);
    //     break;
    //   case 'TODOSW':
    //     this.setTheme(VENTASCLICK_BRAND);
    //     break;
    //   case 'GETNET':
    //     this.setTheme(GSTORE_BRAND);
    //     break; 
    //   case 'TELMEX':
    //     this.setTheme(TELMEX_BRAND);
    //     break; 
    //   case 'TOTALPLAY':
    //     this.setTheme(TOTALPLAY_BRAND);
    //     break; 
    //   case 'MI NEGOCIO DIGITAL':
    //   case 'MI NEGOCIO DIGITAL QUERETARO':
    //       this.setTheme(MND_BRAND);
    //       break; 
    
    //   default:
    //     break;
    // }
  }

  resetTheme() {
    document.documentElement.style.setProperty('--ion-color-primary', this.originalPrimaryColor);
    document.documentElement.style.setProperty(
      '--ion-color-primary-rgb',
      this.originalPrimaryColorRgb
    );
    document.documentElement.style.setProperty(
      '--ion-color-primary-contrast',
      this.originalPrimaryColorContrast
    );
    document.documentElement.style.setProperty(
      '--ion-color-secondary',
      this.originalSecondaryColor
    );
    document.documentElement.style.setProperty(
      '--ion-color-secondary-rgb',
      this.originalSecondaryColorRgb
    );
    document.documentElement.style.setProperty(
      '--ion-color-secondary-contrast',
      this.originalSecondaryColorContrast
    );
    document.documentElement.style.setProperty('--ion-color-tertiary', this.originalTertiaryColor);
    document.documentElement.style.setProperty(
      '--ion-color-tertiary-rgb',
      this.originalTertiaryColorRgb
    );
    document.documentElement.style.setProperty(
      '--ion-color-tertiary-contrast',
      this.originalTertiaryColorContrast
    );
    

    //New variables
    document.documentElement.style.setProperty(
      '--ion-color-button',
      this.originalButtonColor
    );
    document.documentElement.style.setProperty(
      '--ion-color-tab',
      this.originalTabColor
    );
    document.documentElement.style.setProperty(
      '--ion-color-tab-active',
      this.originalTabActiveColor
    );
    document.documentElement.style.setProperty(
      '--ion-color-panel-text',
      this.originalPanelTextColor
    );
    document.documentElement.style.setProperty(
      '--ion-color-contrast-text',
      this.originalContrastTextColor
    );
    document.documentElement.style.setProperty(
      '--ion-color-default-text',
      this.originalDefaultLetterTextColor
    );
    document.documentElement.style.setProperty(
      '--ion-color-default-bg',
      this.originalDefaultLetterBgColor
    );
  }

  private setColors(parsedBranding: Partial<IBrandigApiProps>) {
    //if(parsedBranding.tradename !== 'Boxx CRM'){
    this.myPrimaryColor = '#076cf2';// parsedBranding.primary_color;
    this.myPrimaryColorRgb = this.hexToRgb('#076cf2'); 
    //this.myPrimaryColorContrast = ('#ffffff') ? '#ffffff' : parsedBranding.contrast_text_color;
    this.mySecondaryColor = '#006ef5';
    this.mySecondaryColorRgb = this.hexToRgb('#006ef5');
    this.mySecondaryColorContrast = '#ffffff';
    this.myTertiaryColor = '#006cfd';// parsedBranding.third_color;
    this.myTertiaryColorRgb = this.hexToRgb('#FBBA33');
    this.myTertiaryColorContrast = '#194D33';
    this.myPrimaryTextColor = '#34495E';


    this.myButtonColor = 'linear-gradient(#076cf2, #0f56f4)';// parsedBranding.button_color;
    this.myTabColor = '#194D33';
    this.myPanelTextColor = '#FFFFFF';
    this.myContrastTextColor = '#194D33';
    this.myDefaultLetterTextColor = '#FFFFFF';
    this.myDefaultLetterBgColor = '#4586f2'; //parsedBranding.default_letter_bg_color;
    this.myTabActiveColor = '#218B9A';

    this.MyContactChartNotSpecifiedColor = '#da2322';// parsedBranding.contact_chart_not_specified_color,
    this.MyContactChartProspectColor = '#ddac07'; //parsedBranding.contact_chart_prospect_color,
    this.MyContactChartClientColor = '#00a763'; //parsedBranding.contact_chart_client_color,
    this.MyCustomBackgroundColor = '#ffffff',
    this.MyCustomTitleColor = '#34495e',
    this.MyCustomIconColor = '#ffffff',
    this.MyCustomCheckboxColor = '#ffffff',
    this.MyCustomLabelColor = '#ffffff',
    this.MyCuttonColorContrast = '#ffffff',
    this.MyCustomMenuColor = '#ffffff',

    // this.MyCustomDynamicColorOne = '#ffffff' ? '#ffffff' : '',
    // this.MyCustomDynamicColorTwo = '#34495e' ? '#34495e' : '',
    // this.MyCustomDynamicColorThree = '#ffffff' ? '#ffffff' : '',
    // this.MyCustomDynamicColorFour = '#ffffff' ? '#ffffff' : '',
    // this.MyCustomDynamicColorFive = '#ffffff' ? '#ffffff' : '',

    //primary color set
    document.documentElement.style.setProperty('--ion-color-primary', this.myPrimaryColor);
    document.documentElement.style.setProperty('--ion-color-primary-rgb', this.myPrimaryColorRgb);
    document.documentElement.style.setProperty(
      '--ion-color-primary-contrast',
      '#ffffff'
    );
    //secondary color set
    document.documentElement.style.setProperty('--ion-color-secondary', this.mySecondaryColor);
    document.documentElement.style.setProperty(
      '--ion-color-secondary-rgb',
      this.mySecondaryColorRgb
    );
    document.documentElement.style.setProperty(
      '--ion-color-secondary-contrast',
      this.mySecondaryColorContrast
    );
    //tertiary color set
    document.documentElement.style.setProperty('--ion-color-tertiary', this.myTertiaryColor);
    document.documentElement.style.setProperty('--ion-color-tertiary-rgb', this.myTertiaryColorRgb);
    document.documentElement.style.setProperty(
      '--ion-color-tertiary-contrast',
      this.myTertiaryColorContrast
    );
    //Text color set
    document.documentElement.style.setProperty('--ion-text-color', this.myPrimaryTextColor);

    //New custom variables
    document.documentElement.style.setProperty('--ion-color-button', this.myButtonColor);
    document.documentElement.style.setProperty('--ion-color-tab', this.myTabColor);
    document.documentElement.style.setProperty('--ion-color-panel-text', this.myPanelTextColor);
    document.documentElement.style.setProperty('--ion-color-contrast-text', this.myContrastTextColor);
    document.documentElement.style.setProperty('--ion-color-default-text', this.myDefaultLetterTextColor);
    document.documentElement.style.setProperty('--ion-color-default-bg', this.myDefaultLetterBgColor);
    document.documentElement.style.setProperty('--ion-color-tab-active', this.myTabActiveColor);

    document.documentElement.style.setProperty('--ion-color-contact-chart-not-specified', this.MyContactChartNotSpecifiedColor);
    document.documentElement.style.setProperty('--ion-color-contact-chart-prospect-color', this.MyContactChartProspectColor);
    document.documentElement.style.setProperty('--ion-color-contact-chart-client-color', this.MyContactChartClientColor);
    document.documentElement.style.setProperty('--ion-color-custom-background-color', this.MyCustomBackgroundColor);
    document.documentElement.style.setProperty('--ion-color-custom-title-color', this.MyCustomTitleColor);
    document.documentElement.style.setProperty('--ion-color-custom-icon-color', this.MyCustomIconColor);
    document.documentElement.style.setProperty('--ion-color-custom-checkbox-color', this.MyCustomCheckboxColor);
    document.documentElement.style.setProperty('--ion-color-custom-label-color', this.MyCustomLabelColor);
    document.documentElement.style.setProperty('--ion-color-button-color-contrast', this.MyCuttonColorContrast);
    document.documentElement.style.setProperty('--ion-color-custom-menu-color', this.MyCustomMenuColor);
  
    //custom color 
    document.documentElement.style.setProperty('--ion-color-dynamic-one', this.MyCustomDynamicColorOne);
    document.documentElement.style.setProperty('--ion-color-dynamic-two', this.MyCustomDynamicColorTwo);
    document.documentElement.style.setProperty('--ion-color-dynamic-three', this.MyCustomDynamicColorThree);
    document.documentElement.style.setProperty('--ion-color-dynamic-four', this.MyCustomDynamicColorFour);
    document.documentElement.style.setProperty('--ion-color-dynamic-five', this.MyCustomDynamicColorFive);

    if(parsedBranding.tradename !== 'Boxx CRM'){
      document.documentElement.style.setProperty('--ion-color-button-shade', this.myButtonColor);
      document.documentElement.style.setProperty('--ion-color-button-tint', this.myButtonColor);
    }

    if(this.brandName.toUpperCase().includes('MI NEGOCIO' )){
      //document.documentElement.style.setProperty('--boxx-color-secondary', '#addbda');
      document.documentElement.style.setProperty('--boxx-color-secondary', '#b5e2e4');
    }

    // if(this.brandName.toUpperCase().includes('TODOSW' )){
    //   document.documentElement.style.setProperty('--boxx-header-title-color', '#b5e2e4');
    // }

    if (this.isCordovaOrCapacitor) {
      StatusBar.setBackgroundColor({ color: this.myPrimaryColor });
      StatusBar.setStyle({ style: Style.Dark });
    } else {
      const themeColor = document.querySelector('meta[name=theme-color]');
      const appleThemeColor = document.querySelector(
        'meta[name=apple-mobile-web-app-status-bar-style]'
      );

      themeColor.setAttribute('content', this.myPrimaryColor);
      appleThemeColor.setAttribute('content', this.myPrimaryColor);
    }
  //}


  }

  private setCopies(parsedBranding: Partial<IBrandigApiProps>) {
    this.myComunicationEmail = parsedBranding.comunication_email;
    this.myCountry = parsedBranding.country;
    this.myCurrency = parsedBranding.currency;
    this.myHomePageUrl = parsedBranding.home_pageUrl;
    this.myLanguage = parsedBranding.language;
    this.myLogo = parsedBranding.logo;
    this.myPartnerUrl = parsedBranding.partner_url;
    this.myPrivacyUrl = parsedBranding.privacy_url;
    this.mySignupUrl = parsedBranding.signup_url;
    this.mySupportEmail = parsedBranding.support_email;
    this.myTermsUrl = parsedBranding.terms_url;
    this.myToplineId = parsedBranding.topline_id;
    this.myToplineUid = parsedBranding.topline_uid;
    this.myTradename = parsedBranding.tradename;
    this.myUrlToReset = parsedBranding.url_to_reset;

    this.mySupportUrl = parsedBranding.support_url;
    this.myOptionSupport = (parsedBranding.option_support ? parsedBranding.option_support : 'email');

    this.appCopies.next(true);
  }

  private listenAuthChanges() {
    this.loginStore.User$.subscribe(async user => {
      if (user?.id) {
        this.myUserEmail.next(!environment.production ? user.email : ''); //<-- for testing, remove when dynamic brandig feature is approved

        if (user.branding) {
          this.storage.setItem(BRANDING_LS_KEY, JSON.stringify(user.branding));

          const brand: IBrandigApiProps = {
            button_color: user.branding.button_color,
            primary_color: user.branding.primary_color,
            primary_text_color: user.branding.primary_text_color,
            secondary_color: user.branding.secondary_color,
            secondary_text_color: user.branding.secondary_text_color,
            third_color: user.branding.third_color,
            logo: user.branding.logo,

            comunication_email: user.branding.comunication_email,
            country: user.branding.country,
            currency: user.branding.currency,
            home_pageUrl: user.branding.home_pageUrl,
            language: user.branding.language,
            partner_url: user.branding.partner_url,
            privacy_url: user.branding.privacy_url,
            signup_url: user.branding.signup_url,
            support_email: user.branding.support_email,
            terms_url: user.branding.terms_url,
            topline_id: user.branding.topline_id,
            topline_uid: user.branding.topline_uid,
            tradename: user.branding.tradename,
            url_to_reset: user.branding.url_to_reset,

            tab_color: user.branding.tab_color,
            panel_text_color: user.branding.panel_text_color,
            contrast_text_color: user.branding.contrast_text_color,
            default_letter_text_color: user.branding.default_letter_text_color,
            default_letter_bg_color: user.branding.default_letter_bg_color,
            tab_active_color: user.branding.tab_active_color,
            store_url: user.branding.store_url,

            name: user.branding.tradename,
            primary_color_contrast: user.branding.primary_color_contrast,
            secondary_color_contrast: user.branding.secondary_color_contrast,
            third_color_contrast: user.branding.third_color_contrast,

            support_url: user.branding.support_url,
            option_support: user.branding.option_support,

            contact_chart_not_specified_color: (user.branding.contact_chart_not_specified_color) ? user.branding.contact_chart_not_specified_color : '#9d9d9c',
            contact_chart_prospect_color: (user.branding.contact_chart_prospect_color) ? user.branding.contact_chart_prospect_color : '#dd550c',
            contact_chart_client_color: (user.branding.contact_chart_client_color) ? user.branding.contact_chart_client_color : '#00b5e2',
            custom_background_color: (user.branding.custom_background_color) ? user.branding.custom_background_color : '#0c2340',
            custom_title_color: (user.branding.custom_title_color) ? user.branding.custom_title_color : '#ffffff',
            custom_icon_color: (user.branding.custom_icon_color) ? user.branding.custom_icon_color : '#ffffff',
            custom_checkbox_color: (user.branding.custom_checkbox_color) ? user.branding.custom_checkbox_color : '#ffffff',
            custom_label_color: (user.branding.custom_label_color) ? user.branding.custom_label_color : '#ffffff',
            button_color_contrast: (user.branding.button_color_contrast) ? user.branding.button_color_contrast : '#ffffff',
            custom_menu_color: (user.branding.custom_menu_color) ? user.branding.custom_menu_color : '#ffffff',
            custom_color_dynamic_one: (user.branding.custom_color_dynamic_one) ? user.branding.custom_color_dynamic_one : '#ffffff',
            custom_color_dynamic_two: (user.branding.custom_color_dynamic_two) ? user.branding.custom_color_dynamic_two : '#ffffff',
            custom_color_dynamic_three: (user.branding.custom_color_dynamic_three) ? user.branding.custom_color_dynamic_three : '#ffffff',
            custom_color_dynamic_four: (user.branding.custom_color_dynamic_four) ? user.branding.custom_color_dynamic_four : '#ffffff',
            custom_color_dynamic_five: (user.branding.custom_color_dynamic_five) ? user.branding.custom_color_dynamic_five : '#ffffff',
          };

          this.whenLoggedInAndHasBranding(brand);
        } else {
          this.whenLoggedInAndHasNoBranding();
        }
      } else {
        const storedBranding = await this.storage.getItem(BRANDING_LS_KEY);

        if (storedBranding) {
          this.whenNotLoggedInAndHasBranding(storedBranding);
        } else {
          if (this.savedBranding) {
            this.storage.setItem(BRANDING_LS_KEY, this.savedBranding);
            this.whenNotLoggedInAndHasBranding(this.savedBranding);
          }
        }
      }
    });
  }

  private async whenLoggedInAndHasBranding(branding: Partial<IBrandigApiProps>) {
    this.savedBranding = JSON.stringify(branding);
    await this.storage.setItem(BRANDING_LS_KEY, this.savedBranding);
    this.setTheme(branding);
  }

  private async whenLoggedInAndHasNoBranding() {
    const storedBranding = await this.storage.getItem(BRANDING_LS_KEY);
    if (storedBranding) {
      const theme: IBrandigApiProps = JSON.parse(storedBranding);
      this.setTheme(theme);
    } else {
      this.setTheme();
    }
  }

  private async whenNotLoggedInAndHasBranding(rawBranding: string) {
    const theme: IBrandigApiProps = JSON.parse(rawBranding);
    this.setTheme(theme);
  }

  private hexToRgb(hex: string = ''): string {
    return (
      hex
        .replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i, (m, r, g, b) => '#' + r + r + g + g + b + b)
        .substring(1)
        .match(/.{2}/g) || []
    )
      .map(x => parseInt(x, 16))
      .toString();
  }

  getCurrentBrandName() {
    return this.brandName;
  }

  setBusinessId(business_id){
    this.business_id = business_id;
  }

  getBusinessId(){
    return this.business_id;
  }

  getBrandKey(brand: string){
    let appKey = '';
    switch (brand) {
      case 'BOXX':
      case 'BOXX CRM':
      case '#BOXX':
        appKey = 'Ym94eA';
        break;
      case 'VENTASCLICK':
        appKey = 'VmVudGFzQ2xpY2s';
        break;
      case 'GETNET':
        appKey = 'R2V0TmV0';
        break;
      case 'TOTALPLAY':
        appKey = 'VG90YWxQbGF5';
        break;
      case 'TELMEX':
        appKey = 'dGVsbWV4';
        break;
      case 'BUSINESS WOMAN':
        appKey = 'QnVzaW5lc3MgV29tYW4';
        break;
      case 'MI NEGOCIO DIGITAL':
        appKey = 'TWkgTmVnb2NpbyBEaWdpdGFsIE1JY2hvYWNhbg';
        break;

      case 'TODOSW':
        appKey = 'VG9kb3NX';
        break;
      case 'MI NEGOCIO DIGITAL QUERETARO':
        appKey = 'TWkgbmVnb2NpbyBEaWdpdGFsIFF1ZXJldGFybyA';
        break;
      case 'MUJERES EXTRAORDINARIAS':
        appKey = 'TXVqZXJlcyBFeHRyYW9yZGluYXJpYXM';
        break;
      case 'CASA PUEBLA NEW YORK NEW JERSEY':
        appKey = 'Q0FTQSBQVUVCTEEgTkVXIFlPUksgTkVXIEpFUlNFWQ';
        break;
      case 'PLAZA GARIBALDI':
        appKey = 'UGxhemEgR2FyaWJhbGRp';
        break;
      case 'NELSO FERNANDO CONTRERAS CARRETO':
        appKey = 'TmVsc28gRmVybmFkbyBDb250cmVyYXMgQ2FycmV0bw';
        break;
      case 'MIGRANTES UNIDOS EN CARAVANA A C':
        appKey = 'TWlncmFudGVzIFVuaWRvcyBlbiBDYXJhdmFuYSBBIEM';
        break;
      case 'ANGELES POBLANOS':
        appKey = 'QW5nZWxlcyBQb2JsYW5vcw';
        break;
      case 'UPEXT':
        appKey = 'VVBFWFQ';
        break;
      case 'FUERZA MIGRANTE':
        appKey = 'RnVlcnphIE1pZ3JhbnRl';
        break;
      case 'LOS HIJOS AUSENTES DE CUAUTLA':
        appKey = 'TG9zIEhpam9zIEF1c2VudGVzIGRlIEN1YXV0bGE';
        break;
      case 'MANOS UNIDAS DE AMERICA AC':
        appKey = 'TWFub3MgVW5pZGFzIERlIEFtZXJpY2EgQUM';
        break;        
        
      default:
        break;
    }
    return appKey;
  }

  allowRegistration(brand: string){
    if(brand === 'GETNET' || brand === 'VENTASCLICK' || brand === 'TELMEX' || brand === 'MI NEGOCIO DIGITAL' || brand === 'MI NEGOCIO DIGITAL QUERETARO'){
      return false;
    }  
    return true;
  }
  
  useSpecialFaqs(brand: string){
    if(brand === 'TELMEX' || brand === 'TOTALPLAY' || brand === 'BOXX'){
      return true;
    }  
    return false;
  }

  changeRegisterText(brand: string){
    if(brand === 'TOTALPLAY' || brand === 'TELMEX' || brand === 'TODOSW'
    || brand === 'MUJERES EXTRAORDINARIAS'
    || brand === 'CASA PUEBLA NEW YORK NEW JERSEY'
    || brand === 'PLAZA GARIBALDI'
    || brand === 'NELSO FERNANDO CONTRERAS CARRETO'
    || brand === 'MIGRANTES UNIDOS EN CARAVANA A C'
    || brand === 'ANGELES POBLANOS'
    || brand === 'UPEXT'
    || brand === 'FUERZA MIGRANTE'
    || brand === 'LOS HIJOS AUSENTES DE CUAUTLA'
    || brand === 'MANOS UNIDAS DE AMERICA AC'
    || brand === 'BUSINESS WOMAN' 
    || brand === 'MI NEGOCIO DIGITAL'  
    || brand === 'MI NEGOCIO DIGITAL QUERETARO'
    ){
      return true;
    }  
    return false;
  }

  getRemoteConfigName(brand: string){
    let fbRC = '';
    switch (brand) {
      case 'BOXX':
      case 'BOXX CRM':
      case '#BOXX':
        fbRC = 'boxx_latest_version_';
        break;
      case 'VENTASCLICK':
        fbRC = 'ventasclick_latest_version_';
        break;
      case 'GETNET':
        fbRC = 'getnet_latest_version_';
        break;
      case 'TOTALPLAY':
        fbRC = 'totalplay_latest_version_';
        break;
      case 'TELMEX':
        fbRC = 'telmex_latest_version_';
        break;
      case 'BUSINESS WOMAN':
        fbRC = 'businesswoman_latest_version_';
        break;
        case 'MI NEGOCIO DIGITAL':
          case 'MI NEGOCIO DIGITAL QUERETARO':
            fbRC = 'mng_latest_version_';
            break;
    
          case 'TODOSW':
            fbRC = 'todosw_latest_version_';
            break;

      case 'MI NEGOCIO DIGITAL QUERETARO':
      case 'MUJERES EXTRAORDINARIAS':
      case 'CASA PUEBLA NEW YORK NEW JERSEY':
      case 'PLAZA GARIBALDI':
      case 'NELSO FERNANDO CONTRERAS CARRETO':
      case 'MIGRANTES UNIDOS EN CARAVANA A C':
      case 'ANGELES POBLANOS':
      case 'UPEXT':
      case 'FUERZA MIGRANTE':
      case 'LOS HIJOS AUSENTES DE CUAUTLA':
      case 'MANOS UNIDAS DE AMERICA AC':
        fbRC = 'todosw_latest_version_';
        break;        
        
      default:
        break;
    }
    return fbRC;
  }
}

export const BRANDING_LS_KEY = 'BRANDING';

export interface IBrandigApiProps extends Partial<EnhancedBranding> {
  button_color: string;
  comunication_email: string;
  country: string;
  currency: string;
  home_pageUrl: string;
  language: string;
  logo: string;
  partner_url: string;
  primary_color: string;
  primary_text_color: string;
  privacy_url: string;
  secondary_color: string;
  secondary_text_color: string;
  signup_url: string;
  support_email?: string;
  support_url?: string;
  option_support?: string;
  terms_url: string;
  third_color: string;
  topline_id: string;
  topline_uid: string;
  tradename: string;
  url_to_reset: string;
  tab_color?: string;
  panel_text_color?: string;
  contrast_text_color?: string;
  default_letter_text_color?: string;
  default_letter_bg_color?: string;
  tab_active_color?: string;
  store_url?: string;  
  contact_chart_not_specified_color?: string;
  contact_chart_prospect_color?: string;
  contact_chart_client_color?: string;
  custom_background_color?: string;
  custom_title_color?: string;
  custom_icon_color?: string;
  custom_checkbox_color?: string;
  custom_label_color?: string;
  button_color_contrast?: string;
  custom_menu_color?: string;
  custom_color_dynamic_one?: string;
  custom_color_dynamic_two?: string;
  custom_color_dynamic_three?: string;
  custom_color_dynamic_four?: string;
  custom_color_dynamic_five?: string;
  brand_id?: number | any;
}

interface EnhancedBranding {
  name: string;
  primary_color_contrast: string;
  secondary_color_contrast: string;
  third_color_contrast: string;
}
